import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shield, Target, Phone } from 'lucide-react';
import { companyInfo, stats, serviceImages } from '../mock';
import { Link } from 'react-router-dom';

const Hero = () => {
  return (
    <section id="home" className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 md:py-16 xl:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-6 lg:space-y-8">
            <div className="space-y-4 lg:space-y-6">
              <div className="inline-flex items-center bg-orange-500/20 text-orange-400 px-3 py-1.5 xl:px-4 xl:py-2 rounded-full text-xs xl:text-sm font-medium">
                <Shield className="w-3 h-3 xl:w-4 xl:h-4 mr-2" />
                {companyInfo.experience} d'Excellence
              </div>
              
              <h1 className="text-3xl md:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white leading-tight">
                Expertise en
                <span className="block text-orange-500 mt-2">
                  Télécoms & IT
                </span>
              </h1>
              
              <p className="text-base xl:text-lg text-slate-300 leading-relaxed max-w-xl">
                Excellence en ingénierie des télécommunications et des technologies de l'information. Nous accompagnons les entreprises et les organismes publics dans leurs missions de transformation digitale en offrant des solutions de qualité.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 xl:gap-4">
              <Button 
                asChild
                size="lg" 
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-5 xl:px-8 xl:py-6 text-base xl:text-lg"
              >
                <Link to="/services">
                  Découvrir nos Services
                  <ArrowRight className="ml-2 w-4 h-4 xl:w-5 xl:h-5" />
                </Link>
              </Button>
              
              <Button 
                asChild
                variant="outline" 
                size="lg" 
                className="border-slate-600 text-white hover:bg-slate-800 px-6 py-5 xl:px-8 xl:py-6 text-base xl:text-lg"
              >
                <Link to="/contact">
                  <Phone className="mr-2 w-4 h-4 xl:w-5 xl:h-5" />
                  Nous Contacter
                </Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 xl:gap-6 pt-6 xl:pt-8 border-t border-slate-700">
              <div className="text-center">
                <div className="text-2xl xl:text-4xl font-bold text-white">{stats.experience}</div>
                <div className="text-xl xl:text-sm text-slate-400">Années d'Expérience</div>
              </div>
              <div className="text-center">
                <div className="text-rxl xl:text-4xl font-bold text-white">{stats.projects}</div>
                <div className="text-xs xl:text-sm text-slate-400">Projets Réalisés</div>
              </div>
              <div className="text-center">
                <div className="text-2xl xl:text-4xl font-bold text-white">{stats.clients}</div>
                <div className="text-xs xl:text-sm text-slate-400">Clients Satisfaits</div>
              </div>
            </div>
          </div>

          {/* Right Column - Image & Info Card (Hidden below lg breakpoint) */}
          <div className="hidden lg:block space-y-4">
            {/* Main Card with Image */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Image */}
              <div className="relative">
                <img 
                  src={serviceImages.heroEmployee}
                  alt="Ingénieure télécoms SET-G - Expertise en infrastructure réseau et fibre optique"
                  title="Expertise Télécoms SET-G"
                  loading="eager"
                  className="w-full h-56 object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h3 className="text-white font-bold text-lg">Excellence Technique</h3>
                  <p className="text-slate-200 text-sm">Infrastructure moderne & sécurisée</p>
                </div>
              </div>
              
              {/* Company Name under image */}
              <div className="p-4 bg-slate-50 border-t">
                <p className="text-center text-slate-800 font-semibold text-lg">SET-G</p>
                <p className="text-center text-orange-600 text-sm font-medium">Strategies Engineerings and Technologies Global</p>
              </div>
            </div>

            {/* Engagement Card */}
            <div className="bg-white rounded-xl p-5 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-lg mb-3">NOTRE ENGAGEMENT</h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {companyInfo.mission}
                  </p>
                </div>
              </div>
            </div>

            {/* Certification Badge */}
            <div className="flex items-center justify-center space-x-2 text-slate-400">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Certification Qualité & Excellence</span>
            </div>
          </div>

          {/* Mobile/Tablet: Compact info */}
          <div className="lg:hidden space-y-4">
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center bg-slate-800/50 rounded-full px-3 py-1.5">
                <Shield className="w-4 h-4 text-orange-400 mr-2" />
                <span className="text-white text-sm font-medium">{stats.localites} Localités</span>
              </div>
              <div className="flex items-center bg-slate-800/50 rounded-full px-3 py-1.5">
                <Target className='r-4 h-4 text-orange-400 mr-2' />
                <span className="text-white text-sm font-medium">Expert Audit QoS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
   );
};

export default Hero;